/**
 * BRAND COLLECTIONS SERVICE
 *
 * Brand-scoped rename + merge for the `collectionName` field on user
 * items. Powers the Collections sub-panel inside the brand-manage
 * Polishes tab.
 *
 * Scope: writes are always filtered by `brandLower` matching a brand
 * name the caller provides. Firestore rules enforce that user-item
 * writes only succeed for the user themselves; this client-side code
 * runs as an admin action but the caller is expected to be a
 * verified brand owner whose UI is gated on `isBrandOwner()`.
 * Brand-owner writes to arbitrary user items would fail the rules —
 * so in practice this service is used from the admin CollectionsCleanup
 * page today, and by brand-owner UI once we lift the rule constraint
 * (see plan doc). For now, brand-owner rename via this service is
 * expected to be exercised as an admin action; the intent is to unify
 * once rules are updated to permit brand-owner cross-user writes for
 * these narrow "identity-normalization" fields.
 *
 * The service uses collectionGroup('items') + brandLower filter, plus
 * an in-loop check on `collectionName`. Batched writes at 450 per
 * commit (Firestore's 500-op cap with padding for downstream trigger
 * writes).
 */

import { writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import { fetchBrandUserItems } from '../utils/brandItemQuery';

/** Firestore commit cap: 500 writes; pad to 450 for headroom. */
const BATCH_LIMIT = 450;

export interface BrandCollectionOpSummary {
  scanned: number;
  matched: number;
  written: number;
}

/** Rename a collectionName value to a new one, scoped to items whose
 *  `brandLower` matches. Case-insensitive match on the source name.
 *  `toName` may equal `fromName` (in which case the summary reports 0
 *  writes — caller should short-circuit).
 *
 *  Setting `toName` to an empty string is intentionally rejected; use
 *  a separate "clear" action if you want to remove the collectionName.
 *  Empty target is easy to trigger by mistake and blast-radius is wide.
 */
export async function renameBrandCollection(
  brandName: string,
  fromName: string,
  toName: string
): Promise<BrandCollectionOpSummary> {
  const fromTrimmed = fromName.trim();
  const toTrimmed = toName.trim();
  if (!brandName.trim()) throw new Error('brandName is required');
  if (!fromTrimmed) throw new Error('fromName is required');
  if (!toTrimmed) throw new Error('toName is required (use a clear action to blank it)');

  const docs = await fetchBrandUserItems(brandName);

  let scanned = 0;
  let matched = 0;
  let written = 0;
  let batch = writeBatch(db);
  let ops = 0;

  const commitIfFull = async () => {
    if (ops >= BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  };

  const fromLower = fromTrimmed.toLowerCase();
  for (const d of docs) {
    scanned++;
    // Skip non-user items — brand items don't carry collectionName in
    // the same sense and shouldn't be swept by this operation.
    if (d.ref.parent.parent?.parent?.id !== 'users') continue;
    const data = d.data() as Record<string, unknown>;
    if (data.isDeleted === true) continue;
    const current = typeof data.collectionName === 'string' ? data.collectionName.trim() : '';
    if (current.toLowerCase() !== fromLower) continue;
    matched++;
    if (current === toTrimmed) continue; // already normalized
    batch.update(d.ref, { collectionName: toTrimmed });
    ops++;
    written++;
    await commitIfFull();
  }

  if (ops > 0) await batch.commit();
  return { scanned, matched, written };
}

/** List the distinct collectionName values that appear across a
 *  brand's user items, sorted by usage count desc. Used to populate
 *  the Collections panel's row list without an admin-side sweep. */
export interface BrandCollectionRow {
  name: string;
  count: number;
}

/** Return shape includes diagnostic counts so the UI can distinguish
 *  "no items found for this brand at all" from "items found but none
 *  have a collectionName set". Both are legitimate empty states but
 *  they mean different things. */
export interface BrandCollectionsResult {
  rows: BrandCollectionRow[];
  userItemsFound: number;
  itemsWithCollectionName: number;
}

export async function listBrandCollections(
  brandName: string
): Promise<BrandCollectionsResult> {
  if (!brandName.trim()) {
    return { rows: [], userItemsFound: 0, itemsWithCollectionName: 0 };
  }
  const docs = await fetchBrandUserItems(brandName);
  const counts = new Map<string, number>();
  let userItemsFound = 0;
  let itemsWithCollectionName = 0;
  for (const d of docs) {
    if (d.ref.parent.parent?.parent?.id !== 'users') continue;
    const data = d.data() as Record<string, unknown>;
    if (data.isDeleted === true) continue;
    // Private items shouldn't feed the brand-collections row list — they
    // only belong on the owner's own profile.
    if (data.isPrivate === true) continue;
    userItemsFound++;
    const raw = typeof data.collectionName === 'string' ? data.collectionName.trim() : '';
    if (!raw) continue;
    itemsWithCollectionName++;
    counts.set(raw, (counts.get(raw) ?? 0) + 1);
  }
  const rows = Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return { rows, userItemsFound, itemsWithCollectionName };
}
